import { API_ENDPOINTS } from '@porterin/booking-flow/src/auth/domain/config/api';
import {
  BRAND,
  BRAND_PORTER,
  COUNTRY,
  SOURCE,
} from '@porterin/booking-flow/src/auth/domain/config/constants';
import { NetworkError } from '@porterin/booking-flow/src/auth/domain/types/errors';
import { AgreedDocs } from '@porterin/booking-flow/src/order-review/domain/entities/AgreedDocs';
import { getBookingFlowSource } from '@porterin/booking-flow/src/common/domain/helpers/HeaderHelpers';
import { getLocaleHeaders } from '@porterin/component-library/src/utils/LocaleHelper';

export const createNetworkError = (
  statusCode: number,
  message: string,
): NetworkError => ({
  type: 'NetworkError',
  statusCode,
  message,
});

export const createCheckExistsHeaders = () => {
  const localeHeaders = getLocaleHeaders();

  return {
    brand: BRAND_PORTER,
    source: getBookingFlowSource(),
    ...localeHeaders,
  };
};

export const createHeaders = (webTrackingId: string) => {
  const localeHeaders = getLocaleHeaders();

  return {
    'brand': BRAND,
    'source': getBookingFlowSource(),
    'client-request-uuid': webTrackingId,
    ...localeHeaders,
  };
};

export const createAuthenticatedHeaders = (
  webTrackingId: string,
  authToken: string,
  mobile: string,
) => ({
  ...createHeaders(webTrackingId),
  'mobile': mobile,
  'auth-token': authToken,
});

export const splitFullName = (
  fullName: string,
): [string | null, string | null] => {
  const names = fullName.trim().split(' ');

  if (names.length === 1) {
    return [names[0], null];
  } else {
    return [names[0], names[names.length - 1]];
  }
};

export const createValidateOtpPayload = (
  mobile: string,
  otp: string,
  is_agreed_docs_login: boolean,
  email?: string,
  name?: string,
  whatsappConsent?: boolean,
  customerFrequency?: string,
  agreed_docs?: AgreedDocs,
  referral_code?: string,
  geo_region_id?: string,
) => {
  let commonPayload: any = {
    mobile,
    email: email ?? null,
    otp,
    first_name: splitFullName(name ?? '')[0],
    last_name: splitFullName(name ?? '')[1],
    whatsapp_consent: whatsappConsent ?? null,
    customer_frequency: customerFrequency ?? null,
    created_by: 'customer',
  };
  referral_code && (commonPayload.referral_code = referral_code);
  geo_region_id && (commonPayload.geo_region_id = geo_region_id);

  if (is_agreed_docs_login) {
    return {
      ...commonPayload,
      agreed_docs: agreed_docs?.agreedDocs.map(doc => ({
        created_at: doc.createdAt,
        doc_url: doc.docUrl,
        doc_type: doc.docType,
        updated_at: doc.updatedAt,
      })),
    };
  }

  return {
    mobile,
    email: email ?? null,
    otp,
    first_name: splitFullName(name ?? '')[0],
    last_name: splitFullName(name ?? '')[1],
    whatsapp_consent: whatsappConsent ?? null,
    customer_frequency: customerFrequency ?? null,
    created_by: 'customer',
  };
};

export const getValidateOtpRequestUrl = (option?: 'Whatsapp' | 'SMS') => {
  let requestUrl;
  switch (option) {
    case 'SMS':
      requestUrl = API_ENDPOINTS.CUSTOMER_RESEND_SMS_OTP;
      break;
    case 'Whatsapp':
      requestUrl = API_ENDPOINTS.CUSTOMER_RESEND_WHATSAPP_OTP;
      break;
    default:
      requestUrl = API_ENDPOINTS.CUSTOMER_RESEND_SMS_OTP;
  }

  return requestUrl;
};

export const isEmailError = (errorMessage: string) => {
  return errorMessage.toLowerCase().includes('email');
};

export const createCustomerExistsPayload = (mobile: string) => {
  const utmParams = window.location.search
    ? window.location.search.substring(1)
    : '';

  return {
    mobile,
    utm_params: utmParams,
    all_utm_params: utmParams,
  };
};

export const getAgreedDocsDeclaration = (agreed_docs: AgreedDocs) =>
  `By proceeding, you agree to the [terms of services](${agreed_docs.tncUrl}) and [privacy policy](${agreed_docs.privacyPolicyUrl})`;
